import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import GameModal from "./components/GameModal";
import {
  FiPlayCircle,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiSearch,
  FiList,
  FiUsers,
  FiGrid,
  FiCopy,
  FiCalendar,
} from "react-icons/fi";
import {
  fetchGames,
  deleteGame,
  setGameActive,
  fetchGameEvents,
  fetchGameSummary,
  fetchGameHistory,
  fetchGamePlayers,
} from "../../utils/gameApi";

const TABS = [
  { key: "catalog", label: "Games", icon: FiGrid },
  { key: "history", label: "Play History", icon: FiList },
  { key: "players", label: "Players", icon: FiUsers },
];

const STATUS_BADGE = {
  completed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  disqualified: "bg-rose-100 text-rose-700 border-rose-200",
  started: "bg-amber-100 text-amber-700 border-amber-200",
  expired: "bg-gray-100 text-gray-500 border-gray-200",
};

const RESULT_BADGE = {
  win: "bg-emerald-50 text-emerald-600",
  lose: "bg-gray-100 text-gray-500",
  none: "bg-gray-50 text-gray-400",
};

const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

const Stat = ({ label, value, tone = "text-gray-800", sub = null }) => (
  <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm">
    <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{label}</p>
    <p className={`text-lg font-bold mt-1 ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

const Pager = ({ page, lastPage, total, onPage }) => (
  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
    <p className="text-xs text-gray-400">
      Page {page} of {lastPage} · {total} record{total === 1 ? "" : "s"}
    </p>
    <div className="flex gap-2">
      <button
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50 transition"
      >
        Previous
      </button>
      <button
        onClick={() => onPage(page + 1)}
        disabled={page >= lastPage}
        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50 transition"
      >
        Next
      </button>
    </div>
  </div>
);

/**
 * The games admin surface: a catalog admin can add to, and the play history
 * behind it.
 *
 * Both history tabs are read event-wise. The event picker at the top drives
 * them, and defaults to the event running now — an all-time total across
 * events is not a number anyone acts on, so "All events" is an explicit
 * choice rather than the starting point.
 */
/**
 * A game's round length and break, showing only the halves it has.
 *
 * A formula game (2048) has no timer and a pre-rolled one (Slot Machine) no
 * break, so neither figure is universal — printing a missing one gave
 * "undefineds".
 */
function timeAndBreak(game) {
  const parts = [];
  if (game.gameTime != null) parts.push(`${game.gameTime}s`);
  if (game.cooldownSeconds != null) parts.push(`${game.cooldownSeconds}s`);
  return parts.length ? parts.join(" / ") : "—";
}

const GamesList = () => {
  const [tab, setTab] = useState("catalog");

  // ── Event scope, shared by both report tabs ──────────────────────────
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [summary, setSummary] = useState(null);
  const [scopeEvent, setScopeEvent] = useState(null);

  // ── Catalog ─────────────────────────────────────────────────────────
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(true);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogStatus, setCatalogStatus] = useState("all");
  const [modalGame, setModalGame] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // ── Reports ─────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const searchTimer = useRef(null);

  const loadGames = useCallback(async () => {
    setLoadingGames(true);
    try {
      const res = await fetchGames({ page: 1, limit: 100, search: catalogSearch, status: catalogStatus });
      setGames(res.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load games");
    } finally {
      setLoadingGames(false);
    }
  }, [catalogSearch, catalogStatus]);

  const loadEvents = useCallback(async () => {
    try {
      const res = await fetchGameEvents();
      setEvents(res.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load events");
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const res = await fetchGameSummary({ eventId });
      setSummary(res.data || null);
      setScopeEvent(res.event || null);
    } catch (err) {
      toast.error(err.message || "Failed to load game summary");
    }
  }, [eventId]);

  const loadRows = useCallback(async () => {
    setLoadingRows(true);
    try {
      const params = {
        eventId,
        gameId: gameFilter,
        page,
        limit: 20,
        search: debouncedSearch,
      };
      const res =
        tab === "players"
          ? await fetchGamePlayers(params)
          : await fetchGameHistory({ ...params, status: statusFilter });

      setRows(res.data || []);
      setLastPage(res.pagination?.lastPage || 1);
      setTotal(res.pagination?.total || 0);
      if (res.event !== undefined) setScopeEvent(res.event);
    } catch (err) {
      toast.error(err.message || "Failed to load history");
    } finally {
      setLoadingRows(false);
    }
  }, [tab, eventId, gameFilter, page, debouncedSearch, statusFilter]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (tab !== "catalog") {
      loadSummary();
      loadRows();
    }
  }, [tab, loadSummary, loadRows]);

  // Switching tab or narrowing the scope invalidates the page number: page 7
  // of the old result set is rarely a page of the new one.
  useEffect(() => {
    setPage(1);
  }, [tab, eventId, gameFilter, statusFilter, debouncedSearch]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebouncedSearch(val), 400);
  };

  const handleToggleActive = async (game) => {
    try {
      const res = await setGameActive(game.id, !game.isActive);
      toast.success(res.message || "Game status updated");
      loadGames();
    } catch (err) {
      toast.error(err.message || "Failed to update game status");
    }
  };

  const handleDelete = async (game) => {
    if (!window.confirm(`Delete "${game.name}"? Its play history is kept.`)) return;
    try {
      const res = await deleteGame(game.id);
      toast.success(res.message || "Game deleted");
      loadGames();
    } catch (err) {
      toast.error(err.message || "Failed to delete game");
    }
  };

  const openCreate = () => {
    setModalGame(null);
    setModalOpen(true);
  };

  const openEdit = (game) => {
    setModalGame(game);
    setModalOpen(true);
  };

  const copySessionId = (sessionId) => {
    navigator.clipboard?.writeText(sessionId);
    toast.success("Session ID copied");
  };

  const activeCount = games.filter((g) => g.isActive).length;

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                  <FiPlayCircle size={20} className="text-violet-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">Game Management</h1>
                  <p className="text-xs text-gray-400 mt-1">
                    Add games, set how they score, and review every play event by event.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => (tab === "catalog" ? loadGames() : (loadSummary(), loadRows()))}
                  className="px-3 py-2.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-white transition flex items-center gap-2"
                >
                  <FiRefreshCw size={13} /> Refresh
                </button>
                <button
                  onClick={openCreate}
                  className="px-4 py-2.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <FiPlus size={14} /> Add Game
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 shadow-sm w-fit">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg transition flex items-center gap-2 ${
                    tab === key ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>

            {/* ── Catalog tab ───────────────────────────────────────── */}
            {tab === "catalog" && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <Stat label="Total Games" value={games.length} />
                  <Stat label="Active" value={activeCount} tone="text-emerald-600" />
                  <Stat label="Inactive" value={games.length - activeCount} tone="text-gray-400" />
                  <Stat
                    label="Total Plays"
                    value={games.reduce((sum, g) => sum + (g.stats?.totalPlays || 0), 0)}
                    sub={`${games.reduce((sum, g) => sum + (g.stats?.todayPlays || 0), 0).toLocaleString()} today`}
                  />
                </div>

                <div className="bg-white border border-gray-100 rounded-xl shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <div className="relative flex-1">
                      <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                      <input
                        type="text"
                        value={catalogSearch}
                        onChange={(e) => setCatalogSearch(e.target.value)}
                        placeholder="Search by game name or key"
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      />
                    </div>
                    <select
                      value={catalogStatus}
                      onChange={(e) => setCatalogStatus(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="all">All statuses</option>
                      <option value="active">Active only</option>
                      <option value="inactive">Inactive only</option>
                    </select>
                  </div>

                  {loadingGames ? (
                    <p className="px-4 py-10 text-center text-sm text-gray-400">Loading games...</p>
                  ) : games.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <p className="text-sm text-gray-500 font-medium">No games yet</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Add a game and it appears in the app game list straight away.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                            <th className="px-4 py-3 font-semibold">Game</th>
                            <th className="px-4 py-3 font-semibold">Points Range</th>
                            <th className="px-4 py-3 font-semibold">Time / Break</th>
                            <th className="px-4 py-3 font-semibold">Limits</th>
                            <th className="px-4 py-3 font-semibold">Plays</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {games.map((game) => (
                            <tr key={game.id} className="hover:bg-gray-50/50 transition">
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                                    {game.iconUrl ? (
                                      <img src={game.iconUrl} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <FiPlayCircle size={16} className="text-gray-300" />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-800 truncate">{game.name}</p>
                                    <p className="text-[11px] text-gray-400 font-mono truncate">{game.gameKey}</p>
                                    {!game.route && (
                                      <span className="inline-block mt-1 text-[10px] font-semibold uppercase text-amber-600">
                                        Coming Soon
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {game.minPoints} – {game.maxPoints}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {/*
                                  Each half only when the game has it: a formula
                                  game has no timer and a pre-rolled one has no
                                  break, and interpolating a missing value
                                  printed "undefineds".
                                */}
                                {game.route ? timeAndBreak(game) : "—"}
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-xs">
                                <p>Event: {game.maxPlaysPerEvent ?? "Unlimited"}</p>
                                <p className="text-gray-400">Day: {game.maxPlaysPerDay}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-gray-800 font-medium">
                                  {(game.stats?.totalPlays ?? 0).toLocaleString()}
                                  <span className="text-[11px] text-gray-400 font-normal ml-1">total</span>
                                </p>
                                {/*
                                  Today's figure is the one that says whether a
                                  game is being played now; the lifetime total
                                  cannot. Muted to zero when nobody has played
                                  yet today, rather than hidden, so the row does
                                  not change shape through the day.
                                */}
                                <p
                                  className={`text-[11px] font-medium ${
                                    (game.stats?.todayPlays ?? 0) > 0 ? "text-emerald-600" : "text-gray-300"
                                  }`}
                                >
                                  {(game.stats?.todayPlays ?? 0).toLocaleString()} today
                                </p>
                                <p className="text-[11px] text-gray-400">
                                  {(game.stats?.totalPointsAwarded ?? 0).toLocaleString()} pts awarded
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => handleToggleActive(game)}
                                  className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase border transition ${
                                    game.isActive
                                      ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200"
                                      : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200"
                                  }`}
                                >
                                  {game.isActive ? "Active" : "Inactive"}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => openEdit(game)}
                                    title="Edit game"
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-blue-50 hover:text-blue-600 transition"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(game)}
                                    title="Delete game"
                                    className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ── Report tabs ───────────────────────────────────────── */}
            {tab !== "catalog" && (
              <>
                {/* Event scope */}
                <div className="bg-white border border-gray-100 rounded-xl shadow-sm px-4 py-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-center gap-2 text-gray-500 shrink-0">
                      <FiCalendar size={14} />
                      <span className="text-xs font-semibold uppercase tracking-wide">Event</span>
                    </div>
                    <select
                      value={eventId}
                      onChange={(e) => setEventId(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="">Running event</option>
                      <option value="all">All events</option>
                      {events.map((ev) => (
                        <option key={ev.id} value={ev.id}>
                          {ev.name} ({ev.eventCode}) · {ev.totalPlays} plays
                        </option>
                      ))}
                    </select>
                    {scopeEvent && (
                      <span className="text-[11px] text-gray-400 shrink-0">
                        {formatWhen(scopeEvent.startDate)} → {formatWhen(scopeEvent.endDate)}
                      </span>
                    )}
                  </div>
                </div>

                {summary && (
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <Stat label="Total Plays" value={summary.totals.totalPlays} />
                    <Stat label="Unique Players" value={summary.totals.uniquePlayers} />
                    <Stat label="Points Awarded" value={summary.totals.totalPoints} tone="text-blue-600" />
                    <Stat label="Wins" value={summary.totals.wins} tone="text-emerald-600" />
                    <Stat label="Disqualified" value={summary.totals.disqualified} tone="text-rose-600" />
                  </div>
                )}

                {/* Filters */}
                <div className="bg-white border border-gray-100 rounded-xl shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border-b border-gray-100">
                    <div className="relative flex-1">
                      <FiSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
                      <input
                        type="text"
                        value={search}
                        onChange={handleSearchChange}
                        placeholder="Search by username, email, user ID or session ID"
                        className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      />
                    </div>
                    <select
                      value={gameFilter}
                      onChange={(e) => setGameFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    >
                      <option value="all">All games</option>
                      {games.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                    {tab === "history" && (
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
                      >
                        <option value="all">All statuses</option>
                        <option value="completed">Completed</option>
                        <option value="disqualified">Disqualified</option>
                        <option value="started">Started</option>
                        <option value="expired">Expired</option>
                      </select>
                    )}
                  </div>

                  {loadingRows ? (
                    <p className="px-4 py-10 text-center text-sm text-gray-400">Loading...</p>
                  ) : rows.length === 0 ? (
                    <div className="px-4 py-12 text-center">
                      <p className="text-sm text-gray-500 font-medium">No plays recorded</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Nothing has been played for this event yet.
                      </p>
                    </div>
                  ) : tab === "history" ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                            <th className="px-4 py-3 font-semibold">Player</th>
                            <th className="px-4 py-3 font-semibold">Game</th>
                            <th className="px-4 py-3 font-semibold">Event</th>
                            <th className="px-4 py-3 font-semibold">Score</th>
                            <th className="px-4 py-3 font-semibold">Points</th>
                            <th className="px-4 py-3 font-semibold">Result</th>
                            <th className="px-4 py-3 font-semibold">Played</th>
                            <th className="px-4 py-3 font-semibold">Session</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {rows.map((row) => (
                            <tr key={row.id} className="hover:bg-gray-50/50 transition">
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-800">{row.username || `User #${row.userId}`}</p>
                                <p className="text-[11px] text-gray-400">{row.email || `ID ${row.userId}`}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-gray-700">{row.gameName || row.gameKey}</p>
                                <p className="text-[11px] text-gray-400 font-mono">{row.gameKey}</p>
                              </td>
                              <td className="px-4 py-3 text-gray-600 text-xs">
                                {row.eventName || "—"}
                                <p className="text-[11px] text-gray-400 font-mono">{row.eventCode || ""}</p>
                              </td>
                              <td className="px-4 py-3 text-gray-600">{row.score}</td>
                              <td className="px-4 py-3">
                                <p className="font-semibold text-gray-800">{row.claimedPoints}</p>
                                <p className="text-[11px] text-gray-400">of {row.targetPoints} target</p>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col gap-1 items-start">
                                  <span
                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase border ${
                                      STATUS_BADGE[row.status] || STATUS_BADGE.expired
                                    }`}
                                  >
                                    {row.status}
                                  </span>
                                  {row.result && row.result !== "none" && (
                                    <span
                                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase ${
                                        RESULT_BADGE[row.result] || RESULT_BADGE.none
                                      }`}
                                    >
                                      {row.result}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {formatWhen(row.completedAt || row.startedAt)}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => copySessionId(row.sessionId)}
                                  title={row.sessionId}
                                  className="text-[11px] text-gray-400 hover:text-blue-600 font-mono flex items-center gap-1 transition"
                                >
                                  <FiCopy size={11} /> Copy
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                            <th className="px-4 py-3 font-semibold">Player</th>
                            <th className="px-4 py-3 font-semibold">Game</th>
                            <th className="px-4 py-3 font-semibold">Times Played</th>
                            <th className="px-4 py-3 font-semibold">Total Points</th>
                            <th className="px-4 py-3 font-semibold">Best Round</th>
                            <th className="px-4 py-3 font-semibold">Wins</th>
                            <th className="px-4 py-3 font-semibold">Disqualified</th>
                            <th className="px-4 py-3 font-semibold">Last Played</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {rows.map((row) => (
                            <tr key={`${row.userId}-${row.gameId}`} className="hover:bg-gray-50/50 transition">
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-800">{row.username || `User #${row.userId}`}</p>
                                <p className="text-[11px] text-gray-400">{row.email || `ID ${row.userId}`}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-gray-700">{row.gameName}</p>
                                <p className="text-[11px] text-gray-400 font-mono">{row.gameKey}</p>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm font-bold text-gray-800">{row.totalPlays}</span>
                                <span className="text-[11px] text-gray-400 ml-1">time{row.totalPlays === 1 ? "" : "s"}</span>
                              </td>
                              <td className="px-4 py-3 font-semibold text-blue-600">{row.totalPoints}</td>
                              <td className="px-4 py-3 text-gray-600">{row.bestPoints}</td>
                              <td className="px-4 py-3 text-emerald-600 font-medium">{row.wins}</td>
                              <td className="px-4 py-3 text-rose-600">{row.disqualified}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{formatWhen(row.lastPlayedAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {rows.length > 0 && <Pager page={page} lastPage={lastPage} total={total} onPage={setPage} />}
                </div>

                {/* Per-game breakdown for the selected event */}
                {summary && summary.perGame.length > 0 && (
                  <div className="bg-white border border-gray-100 rounded-xl shadow-sm">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                        Per Game {scopeEvent ? `· ${scopeEvent.name}` : "· All events"}
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                            <th className="px-4 py-3 font-semibold">Game</th>
                            <th className="px-4 py-3 font-semibold">Plays</th>
                            <th className="px-4 py-3 font-semibold">Players</th>
                            <th className="px-4 py-3 font-semibold">Points</th>
                            <th className="px-4 py-3 font-semibold">Wins</th>
                            <th className="px-4 py-3 font-semibold">Disqualified</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {summary.perGame.map((g) => (
                            <tr key={g.gameId || g.gameKey} className="hover:bg-gray-50/50 transition">
                              <td className="px-4 py-3 text-gray-700">{g.gameName}</td>
                              <td className="px-4 py-3 text-gray-600">{g.totalPlays}</td>
                              <td className="px-4 py-3 text-gray-600">{g.uniquePlayers}</td>
                              <td className="px-4 py-3 font-semibold text-blue-600">{g.totalPoints}</td>
                              <td className="px-4 py-3 text-emerald-600">{g.wins}</td>
                              <td className="px-4 py-3 text-rose-600">{g.disqualified}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {modalOpen && (
        <GameModal game={modalGame} onClose={() => setModalOpen(false)} onSaved={loadGames} />
      )}
    </div>
  );
};

export default GamesList;
